/* eslint-disable no-console */
import cloneDeep from 'lodash/cloneDeep';
import { get, isEmpty, startsWith } from 'lodash';
import AnalyticsService from './service';
import {
  clientDashboardCategory, debugEnabled, websiteCategory, blogCategory,
} from '../index';
import AnalyticsEvent from '../events/event';

export default class Mixpanel extends AnalyticsService {
  get name() {
    return 'Mixpanel';
  }

  get enabled() {
    return window && window.mixpanel && window.mixpanel.track;
  }

  get mp() {
    // does nothing if not enabled
    if (!this.enabled) {
      return () => ({
        track: () => {
        },
        alias: () => {
        },
        identify: () => {
        },
        people: () => ({
          set: () => {
          },
        }),
      });
    }

    return window.mixpanel;
  }

  alias(clientId) {
    if (debugEnabled) {
      console.info(`mixpanel aliasing: ${clientId}`);
    }

    this.mp.alias(clientId);
  }

  reIdentify(identity) {
    const peopleProps = {};

    const email = get(identity, 'email');
    const firstName = get(identity, 'first_name');
    const lastName = get(identity, 'last_name');

    if (!isEmpty(email)) {
      peopleProps.$email = email;
    }
    if (!isEmpty(firstName)) {
      peopleProps.$first_name = firstName;
    }
    if (!isEmpty(lastName)) {
      peopleProps.$last_name = lastName;
    }

    if (debugEnabled) {
      console.info('mixpanel reIdentifying', peopleProps);
    }

    if (!isEmpty(peopleProps)) {
      this.mp.people.set(peopleProps);
      this.mp.identify();
    }
  }

  _track(event) {
    if (!this.enabled) {
      return;
    }

    if (!event instanceof AnalyticsEvent) {
      console.error('mixpanel _track method can only receive events that are instances of AnalyticsEvent', event);
      return;
    }

    if (debugEnabled) {
      console.info(`mixpanel tracking event: ${event.category}: ${event.name}`, event.data);
    }

    // mixpanel mutates the event.data to add the token to the event data
    // but we don't want to accidentally send that data to the other analytics services
    // so we have to clone it
    this.mp.track(`${event.category}: ${event.name}`, cloneDeep(event.data));
  }

  trackWebsitePageView(inertiaProps) {
    if (startsWith(this.currentPagePath, '/blog')) {
      return this.trackBlogPageView(inertiaProps);
    }

    this._track(new AnalyticsEvent({
      category: websiteCategory,
      name: 'Page Viewed',
      data: {
        'Page Title': this.currentPageTitle,
        path: this.currentPagePath,
      },
      services: [
        'mixpanel',
      ],
    }));
  }

  trackBlogPageView(inertiaProps) {
    const currentPost = get(inertiaProps, 'blogPost.postMeta.slug');
    const currentPostCategory = get(inertiaProps, 'blogPost.postCategory.slug');
    const currentCategory = get(inertiaProps, 'blogCategories.currentCategory');
    if (currentPost) {
      this._track(new AnalyticsEvent({
        category: blogCategory,
        name: 'Viewed Post',
        data: {
          Post: currentPost,
          Category: currentPostCategory,
        },
        services: [
          'mixpanel',
        ],
      }));
      return;
    }
    if (currentCategory === 'all') {
      this._track(new AnalyticsEvent({
        category: blogCategory,
        name: 'Viewed Blog Home Page',
        data: {},
        services: [
          'mixpanel',
        ],
      }));
    } else {
      this._track(new AnalyticsEvent({
        category: blogCategory,
        name: 'Viewed Category Page',
        data: {
          Category: currentCategory,
        },
        services: [
          'mixpanel',
        ],
      }));
    }
  }

  trackClientDashboardPageView(pageName) {
    this._track(new AnalyticsEvent({
      category: clientDashboardCategory,
      name: `Viewed ${pageName}`,
      data: {
        path: this.currentPagePath,
      },
      services: [
        'mixpanel',
      ],
    }));
  }
}
